import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CreditCard, CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const INVENTORY_ADDON_SQUARE_LINK = "https://square.link/u/Da5KNAtB";

const formatDate = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });
  } catch {
    return iso;
  }
};

export const InventoryAddonCheckout = ({ authHeaders, onRefresh, compact = false }) => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [buying, setBuying] = useState(false);
  const [canceling, setCanceling] = useState(false);

  const fetchStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/inventory/addon/status`, { headers: authHeaders });
      setStatus(res.data);
    } catch (err) {
      console.error("Error fetching inventory addon status:", err);
    } finally {
      setLoading(false);
    }
  }, [authHeaders]);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  const handleBuy = async () => {
    setBuying(true);
    try {
      await axios.post(`${API}/inventory/addon/pending-subscription`, {}, { headers: authHeaders });
      window.location.href = INVENTORY_ADDON_SQUARE_LINK;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start checkout. Please try again.");
      setBuying(false);
    }
  };

  const handleCancel = async () => {
    if (!window.confirm("Cancel the Inventory add-on? You'll keep access until the end of your current billing period.")) return;
    setCanceling(true);
    try {
      await axios.post(`${API}/inventory/addon/cancel-subscription`, {}, { headers: authHeaders });
      toast.success("Canceled. You'll have access until your billing period ends.");
      await fetchStatus();
      onRefresh?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to cancel. Please try again.");
    } finally {
      setCanceling(false);
    }
  };

  if (loading || !status || !status.addonApplicable) return null;

  if (status.active && status.canceledAt) {
    return (
      <div data-testid="inventory-addon-canceled-state" className={compact ? "flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-1.5" : "flex flex-col items-center gap-2"}>
        <p className="text-sm font-semibold text-amber-700">
          Canceled — access ends {formatDate(status.expiresAt)}
        </p>
      </div>
    );
  }

  if (status.active) {
    return (
      <div data-testid="inventory-addon-active-state" className={compact
        ? "flex items-center gap-3 bg-cyan-50 border border-cyan-200 rounded-lg px-3 py-1.5"
        : "rounded-xl border-2 border-cyan-400 bg-cyan-50 p-4 flex items-center justify-between gap-3 w-full max-w-md"}>
        <div className="flex items-center gap-2">
          <CheckCircle2 className="w-4 h-4 flex-shrink-0 text-cyan-600" />
          <p className="text-sm font-semibold text-cyan-800">
            Inventory Add-on active — renews {formatDate(status.expiresAt)}
          </p>
        </div>
        <Button onClick={handleCancel} disabled={canceling} variant="outline" size="sm"
          className="border-rose-300 text-rose-600 hover:bg-rose-50 flex-shrink-0" data-testid="inventory-addon-cancel-button">
          {canceling ? <><Loader2 className="w-3 h-3 mr-2 animate-spin" />Canceling...</> : <><XCircle className="w-4 h-4 mr-2" />Cancel at anytime</>}
        </Button>
      </div>
    );
  }

  return (
    <div data-testid="inventory-addon-checkout" className="flex flex-col items-center gap-3">
      <Button data-testid="inventory-addon-buy-button" onClick={handleBuy} disabled={buying}>
        <CreditCard className="w-4 h-4 mr-2" />
        {buying ? "Opening checkout…" : "Unlock for $14.99/mo"}
      </Button>
      <p className="text-sm text-slate-500">After subscribing on Square, return here and refresh to activate.</p>
    </div>
  );
};

export default InventoryAddonCheckout;
